import { useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BranchProvider } from './context/BranchContext';
import { SimpleToastProvider, useSimpleToast, setToastFunction } from './components/ui/simple-toast';
import ErrorBoundary from './components/ErrorBoundary';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import POS from './pages/POS';
import Tables from './pages/Tables';
import Products from './pages/Products';
import SpecialProducts from './pages/SpecialProducts';
import Supplies from './pages/Supplies';
import ProcessedSupplies from './pages/ProcessedSupplies';
import Suppliers from './pages/Suppliers';
import Purchases from './pages/Purchases';
import Inventory from './pages/Inventory';
import Kardex from './pages/Kardex';
import CashRegister from './pages/CashRegister';
import Reports from './pages/Reports';
import AIInsights from './pages/AIInsights';
import Settings from './pages/Settings';
import KitchenMonitor from './pages/KitchenMonitor';
import Customers from './pages/Customers';
import Users from './pages/Users';
import Egresos from './pages/Egresos';
import Production from './pages/Production';
import SalesReport from './pages/SalesReport';
import FinancialReports from './pages/FinancialReports';
import InventoryAdjustments from './pages/InventoryAdjustments';
import ReportsHub from './pages/ReportsHub';
import SalesComparison from './pages/SalesComparison';
import AreaConsumption from './pages/AreaConsumption';
import Branches from './pages/Branches';
import BranchReports from './pages/BranchReports';
import BranchDistribution from './pages/BranchDistribution';
import DeliveryCRM from './pages/DeliveryCRM';
import CRMIntelligence from './pages/CRMIntelligence';
import LoyaltyManager from './pages/LoyaltyManager';
import CustomerPortal from './pages/CustomerPortal';
import MenuPublic from './pages/MenuPublic';
import MenuAdmin from './pages/MenuAdmin';
import PromoStudio from './pages/PromoStudio';
import ExtrasManager from './pages/ExtrasManager';
import PurchaseAgent from './pages/PurchaseAgent';
import './App.css';

// Toast initializer component
const ToastInitializer = () => {
  const toast = useSimpleToast();
  useEffect(() => {
    setToastFunction(toast);
  }, [toast]);
  return null;
};

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const { processOAuthSession } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  if (!hasProcessed.current) {
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
    
    if (sessionId) {
      processOAuthSession(sessionId)
        .then((user) => {
          navigate('/dashboard', { state: { user }, replace: true });
        })
        .catch((error) => {
          console.error('OAuth error:', error);
          navigate('/login', { replace: true });
        });
    } else {
      navigate('/login', { replace: true });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="animate-pulse text-muted-foreground">Procesando autenticación...</div>
    </div>
  );
};

// Role permissions for route protection
const ROLE_ROUTE_PERMISSIONS = {
  admin: ['*'],
  gerente: ['dashboard', 'pos', 'cash_register', 'inventory', 'reports', 'customers', 'suppliers', 'products', 'kitchen', 'tables', 'supplies', 'purchases', 'settings', 'users'],
  cajero: ['dashboard', 'pos', 'cash_register'],
  mesero: ['pos', 'tables'],
  cocinero: ['dashboard', 'kitchen'],
  almacenista: ['dashboard', 'inventory', 'supplies', 'purchases']
};

const ROUTE_PERMISSION_MAP = {
  '/dashboard': 'dashboard',
  '/pos': 'pos',
  '/kitchen-monitor': 'kitchen',
  '/tables': 'tables',
  '/cash-register': 'cash_register',
  '/supplies': 'supplies',
  '/processed-supplies': 'supplies',
  '/purchases': 'purchases',
  '/inventory': 'inventory',
  '/products': 'products',
  '/suppliers': 'suppliers',
  '/customers': 'customers',
  '/reports': 'reports',
  '/financial-reports': 'reports',
  '/ai-insights': 'reports',
  '/settings': 'settings',
  '/users': 'users'
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check role permissions for route access
  const userRole = user?.role || 'mesero';
  const allowedRoutes = ROLE_ROUTE_PERMISSIONS[userRole] || [];
  const requiredPermission = ROUTE_PERMISSION_MAP[location.pathname];

  if (requiredPermission && !allowedRoutes.includes('*') && !allowedRoutes.includes(requiredPermission)) {
    return <Navigate to="/pos" replace />;
  }

  return children;
};

const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id synchronously
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/mi-tarjeta" element={<CustomerPortal />} />
      <Route path="/menu" element={<MenuPublic />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/menu-admin" element={<ProtectedRoute><MenuAdmin /></ProtectedRoute>} />
      <Route path="/promo-studio" element={<ProtectedRoute><PromoStudio /></ProtectedRoute>} />
      <Route path="/pos" element={<ProtectedRoute><POS /></ProtectedRoute>} />
      <Route path="/tables" element={<ProtectedRoute><Tables /></ProtectedRoute>} />
      <Route path="/products" element={<ProtectedRoute><Products /></ProtectedRoute>} />
      <Route path="/extras" element={<ProtectedRoute><ExtrasManager /></ProtectedRoute>} />
      <Route path="/special-products" element={<ProtectedRoute><SpecialProducts /></ProtectedRoute>} />
      <Route path="/supplies" element={<ProtectedRoute><Supplies /></ProtectedRoute>} />
      <Route path="/processed-supplies" element={<ProtectedRoute><ProcessedSupplies /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><Suppliers /></ProtectedRoute>} />
      <Route path="/purchases" element={<ProtectedRoute><Purchases /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
      <Route path="/kardex" element={<ProtectedRoute><Kardex /></ProtectedRoute>} />
      <Route path="/inventory-adjustments" element={<ProtectedRoute><InventoryAdjustments /></ProtectedRoute>} />
      <Route path="/production" element={<ProtectedRoute><Production /></ProtectedRoute>} />
      <Route path="/cash-register" element={<ProtectedRoute><CashRegister /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
      <Route path="/reports-hub" element={<ProtectedRoute><ReportsHub /></ProtectedRoute>} />
      <Route path="/sales-comparison" element={<ProtectedRoute><SalesComparison /></ProtectedRoute>} />
      <Route path="/area-consumption" element={<ProtectedRoute><AreaConsumption /></ProtectedRoute>} />
      <Route path="/sales-report" element={<ProtectedRoute><SalesReport /></ProtectedRoute>} />
      <Route path="/financial-reports" element={<ProtectedRoute><FinancialReports /></ProtectedRoute>} />
      <Route path="/egresos" element={<ProtectedRoute><Egresos /></ProtectedRoute>} />
      <Route path="/ai-insights" element={<ProtectedRoute><AIInsights /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/kitchen-monitor" element={<ProtectedRoute><KitchenMonitor /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
      <Route path="/branches" element={<ProtectedRoute><Branches /></ProtectedRoute>} />
      <Route path="/branch-reports" element={<ProtectedRoute><BranchReports /></ProtectedRoute>} />
      <Route path="/distribution" element={<ProtectedRoute><BranchDistribution /></ProtectedRoute>} />
      <Route path="/crm-domicilio" element={<ProtectedRoute><DeliveryCRM /></ProtectedRoute>} />
      <Route path="/crm-inteligente" element={<ProtectedRoute><CRMIntelligence /></ProtectedRoute>} />
      <Route path="/programa-lealtad" element={<ProtectedRoute><LoyaltyManager /></ProtectedRoute>} />
      <Route path="/agente-compras" element={<ProtectedRoute><PurchaseAgent /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <SimpleToastProvider>
          <ToastInitializer />
          <AuthProvider>
            <BranchProvider>
              <AppRouter />
            </BranchProvider>
          </AuthProvider>
        </SimpleToastProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
